import { IssueCaseDoc, IssueCaseStatusCode, IssueProduct, IssueSourceType } from '@gooduncles/gu-app-schema';
import { Tag } from 'antd';
import { FC, Fragment, useMemo } from 'react';
import {
  issueCaseKindColor,
  issueCaseKindLabel,
  issueCaseKindTagColor,
  normalizeCaseKind,
} from 'src/constants/issue.constants';

import { formatNumber } from 'src/utils/common';
import { formatDate } from 'src/utils/date-util';
import { getCategoryLabel } from 'src/utils/issue/issue-data-util';
import {
  getIssueCaseLabel,
  isIssueCaseEditable,
  issueCaseOptions,
  issueCaseStatusBadgeMap,
} from 'src/utils/issue/issue-util';

import useIssueCaseActions from 'src/hooks/useIssueCaseActions';

import useIssueTasksForCase from 'src/hooks/subscription/useIssueTasksForCase';

import { AddButton } from 'src/atomic-components/atoms/AddButton';
import IssuePanelDivider from 'src/atomic-components/atoms/Divider/IssuePanelDivider';
import IssueCardHeader from 'src/atomic-components/atoms/IssueCardItem/IssueCardHeader';
import { InfoItem } from 'src/atomic-components/atoms/IssueInfoItem';
import { IssueStatusBadge } from 'src/atomic-components/atoms/IssueStatusBadge';
import AssignedIssueItem from 'src/atomic-components/molecules/AssignedIssueItem';
import IssueCaseInfoSection from 'src/atomic-components/molecules/IssueCaseCard/IssueCaseInfoSection';
import { IssueProductItem } from 'src/atomic-components/molecules/IssueProductItem/IssueProductItem';
import IssueProductsEditModal from 'src/atomic-components/molecules/IssueProductsEditModal/IssueProductsEditModal';
import TaskSummaryCard from 'src/atomic-components/molecules/TaskSummaryCard/TaskSummaryCard';
import LinkedMessagesSection from 'src/components/Issue/MessageLink/LinkedMessagesSection';

interface CasedIssueCaseCardProps {
  issueCaseDoc: IssueCaseDoc;
  issueCases: IssueCaseDoc[];
  onIssueCaseClick?: (issueCaseId: string) => void;
}
const CasedIssueCaseCard: FC<CasedIssueCaseCardProps> = ({ issueCaseDoc, issueCases, onIssueCaseClick }) => {
  const { issueTasks } = useIssueTasksForCase(issueCaseDoc._id);
  const caseKind = normalizeCaseKind(issueCaseDoc.caseKind);
  const showIssueCases = issueCases && issueCases.length > 0;
  const hasIssueTasks = issueTasks && issueTasks.length > 0;
  const caseProducts: IssueProduct[] = issueCaseDoc.products ?? [];

  const canCreateIssueCase = useMemo(
    () => issueCaseDoc.status === IssueCaseStatusCode.IN_PROGRESS || issueCaseDoc.status === IssueCaseStatusCode.CLOSED,
    [issueCaseDoc.status],
  );

  const { createNewIssueCase, updateIssueCaseProducts } = useIssueCaseActions({
    getSourceParams: () => ({
      sourceType: IssueSourceType.ISSUE,
      sourceId: issueCaseDoc._id,
      sourceReferenceDate: formatDate(issueCaseDoc.registeredAt, 'yyyy-MM-dd'),
      storeId: issueCaseDoc.storeId,
      storeName: issueCaseDoc.storeName,
    }),
    canCreate: () => ({
      allowed: canCreateIssueCase,
      message: '이슈 케이스는 처리 중 또는 종료 상태에서만 추가할 수 있습니다.',
    }),
  });

  return (
    <div>
      <IssueCardHeader
        id={issueCaseDoc._id}
        title={getCategoryLabel(issueCaseDoc.category)}
        spanProps={{ style: { color: issueCaseKindColor[caseKind] } }}
        badge={
          caseKind === 'request' ? (
            <Tag color={issueCaseKindTagColor.request} style={{ margin: 0 }}>
              {issueCaseKindLabel.request}
            </Tag>
          ) : undefined
        }
      />
      <InfoItem
        label='상태'
        value={
          <IssueStatusBadge
            text={issueCaseStatusBadgeMap[issueCaseDoc.status].text}
            status={issueCaseStatusBadgeMap[issueCaseDoc.status].status}
          />
        }
      />
      <IssueCaseInfoSection issueCaseDoc={issueCaseDoc} editable={false} />
      <LinkedMessagesSection issueCase={issueCaseDoc} editable={isIssueCaseEditable(issueCaseDoc)} />
      <IssuePanelDivider />
      {showIssueCases && (
        <>
          {issueCases.map((childIssueCase) => (
            <Fragment key={childIssueCase._id}>
              <AssignedIssueItem
                label={getIssueCaseLabel(childIssueCase)}
                color='orange'
                onLabelClick={() => onIssueCaseClick?.(childIssueCase._id)}
                rightSection={
                  <IssueProductsEditModal
                    color='orange'
                    title={getIssueCaseLabel(childIssueCase)}
                    buttonText={childIssueCase.products ? `${childIssueCase.products.length}건` : '상품 등록'}
                    currentProducts={childIssueCase.products ?? []}
                    selectableProducts={caseProducts}
                    editable={isIssueCaseEditable(childIssueCase)}
                    onUpdateProducts={(products) => updateIssueCaseProducts(childIssueCase, products)}
                  />
                }
              />
              <LinkedMessagesSection issueCase={childIssueCase} editable={isIssueCaseEditable(childIssueCase)} />
              {childIssueCase.products
                ? childIssueCase.products.map((p, index) => (
                    <IssueProductItem
                      key={childIssueCase._id + p.productId}
                      productName={p.productName}
                      unitPrice={p.price}
                      quantity={p.quantity}
                      totalPrice={p.price * p.quantity}
                      showHierarchy={true}
                      isLast={childIssueCase.products?.length ? index === childIssueCase.products.length - 1 : false}
                    />
                  ))
                : null}
            </Fragment>
          ))}

          <IssuePanelDivider />
        </>
      )}
      <AddButton
        text='새로운 이슈 케이스'
        color='orange'
        onSelect={createNewIssueCase}
        options={issueCaseOptions}
        disabled={!canCreateIssueCase}
      />
      <IssuePanelDivider />
      <InfoItem label='이슈 상품' value={caseProducts.length + '건'} />
      {caseProducts.length > 0
        ? caseProducts.map((product, index) => (
            <IssueProductItem
              key={issueCaseDoc._id + product.productId}
              productName={product.productName}
              unitPrice={product.price}
              quantity={product.quantity}
              totalPrice={product.price * product.quantity}
              isLast={index === caseProducts.length - 1}
              isHighlight={issueCases.some((issueCase) =>
                issueCase.products?.some((p) => p.productId === product.productId),
              )}
            />
          ))
        : null}
      <InfoItem label='총 금액' value={`${formatNumber(issueCaseDoc.amount?.totalAmount ?? 0)}원`} />
      <IssuePanelDivider />
      <InfoItem label='이슈 태스크' value={issueTasks.length + '건'} />
      {hasIssueTasks && (
        <>
          {issueTasks.map((issueTask) => (
            <TaskSummaryCard key={issueTask._id} task={issueTask} />
          ))}
          <IssuePanelDivider />
        </>
      )}
    </div>
  );
};

export default CasedIssueCaseCard;
export type { CasedIssueCaseCardProps };
